import { forwardRef, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { Icon, IconButton, UiLinkProvider, cx, type UiLinkProps } from '@algoboo/ui'
import { WalletButton, useSession } from './session'
import { NotificationBell } from './NotificationBell'
import { LEGAL, MORE, PRIMARY, isActive, isMarketing, type NavItem } from '../lib/nav'
import { useKeyboardInset } from '../lib/keyboard'
import { ActivityTicker } from './social/ActivityTicker'
import { MoreMenu } from './AccountMenu'

/** Router-aware link for kit components: internal paths navigate client-side. */
const RouterLink = forwardRef<HTMLAnchorElement, UiLinkProps>(function RouterLink({ href, ...rest }, ref) {
  if (/^(https?:|mailto:)/.test(href) || href.startsWith('/api') || href.startsWith('/brand')) return <a ref={ref} href={href} {...rest} />
  const [path, qs] = href.split('?')
  const search = qs ? Object.fromEntries(new URLSearchParams(qs)) : undefined
  return <Link ref={ref} to={path as never} {...(search ? { search: search as never } : {})} {...(rest as object)} />
})

/** Routes that fill the screen and scroll inside themselves (the page never scrolls): the desk and the rooms. */
const FILL = ['/desk', '/rooms', '/rooms/']

/**
 * The page's key: a new page mounts fresh and enters with its animation, but `/rooms` and its rooms are one page,
 * the `/rooms` layout, so the room list stays mounted from room to room (lane BOO1).
 */
export function pageKey(path: string): string {
  return /^\/rooms(\/[0-9a-f-]{36})?\/?$/i.test(path) ? '/rooms' : path
}

/** A room (`/rooms/<uuid>`) fills the screen like the desk (lane CHATWEB1). */
export function fillsScreen(path: string): boolean {
  return FILL.includes(path) || /^\/rooms\/[0-9a-f-]{36}\/?$/i.test(path)
}

/*
 * Layout by width (lane IA):
 *   < 768     top bar (brand, search, bell, avatar) + bottom tab bar (Feed, Agents, Desk, Tokens, Portfolio)
 *   768-1023  top bar + icon rail
 *   >= 1024   top bar + rail with labels
 * One scroll container per screen: the document on ordinary pages (the top bar is sticky, the rail fixed and never
 * scrolls), the thread pane on the desk (the page is locked to the viewport). All variants render and CSS picks.
 */
export function Shell({ children }: { children: ReactNode }) {
  const path = useRouterState({ select: (s) => s.location.pathname })
  const { me, loading } = useSession()
  useKeyboardInset()
  const fill = fillsScreen(path)
  const footer = isMarketing(path) && !(path === '/' && (me || loading))

  return (
    <UiLinkProvider value={RouterLink}>
      <a href="#main" className="sr-only z-50 rounded-md bg-ink px-3 py-2 text-ink-inverse focus:not-sr-only focus:fixed focus:left-3 focus:top-3">
        Skip to content
      </a>
      <Header signedIn={!!me} />
      <Rail path={path} />
      <div className="md:pl-[72px] lg:pl-[200px]">
        {/* FOMO layer: the live activity tape under the top bar, 1024px and up (components/social). */}
        {!fill && <ActivityTicker />}
        <main
          id="main"
          tabIndex={-1}
          data-fill={fill ? 'true' : undefined}
          className={cx('relative w-full outline-none', fill ? 'rh-fill overflow-hidden' : 'mx-auto min-h-[60vh] max-w-[1248px] px-4 pb-[calc(88px+env(safe-area-inset-bottom))] pt-5 sm:px-6 sm:pt-6 md:pb-16')}
        >
          <div key={pageKey(path)} className={cx('rh-route', fill && 'h-full')}>
            {children}
          </div>
        </main>
        {footer && <Footer />}
      </div>
      <BottomTabs path={path} />
    </UiLinkProvider>
  )
}

function Brand() {
  return (
    <Link to="/" className="rh-hit group flex shrink-0 items-center gap-2 rounded-lg pr-1" aria-label="algo.boo home">
      {/* the static mark (same as the app icon): the header never waits for the 3D renderer */}
      <img src="/icons/mark.svg" alt="" width={28} height={28} className="rh-mascot h-7 w-7" />
      <span className="font-display text-[16px] font-semibold tracking-[-0.02em] text-ink max-[359px]:sr-only">algo.boo</span>
    </Link>
  )
}

function Header({ signedIn }: { signedIn: boolean }) {
  const [searching, setSearching] = useState(false)
  return (
    <header className="rh-glass sticky top-0 z-30 border-b border-line pt-[env(safe-area-inset-top)]">
      <div className="flex h-14 items-center gap-2 px-4 sm:gap-3 sm:px-6">
        <Brand />
        <HeaderSearch className="ml-2 hidden max-w-sm flex-1 sm:flex lg:ml-10" />
        <div className="ml-auto flex min-w-0 items-center gap-1 sm:gap-1.5">
          <IconButton icon="search" label="Search" className="sm:hidden" size={40} onClick={() => setSearching(true)} aria-expanded={searching} />
          {signedIn && (
            <Link to="/rooms" aria-label="Rooms" title="Rooms" data-testid="rooms-link" className="rh-hit inline-flex h-10 w-10 items-center justify-center rounded-[8px] text-ink-3 hover:bg-sunken hover:text-ink">
              <Icon name="chat" size={18} />
            </Link>
          )}
          <NotificationBell />
          <WalletButton />
          {!signedIn && <MoreMenu />}
        </div>
      </div>
      {searching && (
        <div className="border-t border-line px-4 py-2 sm:hidden">
          <HeaderSearch autoFocus onDone={() => setSearching(false)} className="flex" />
        </div>
      )}
    </header>
  )
}

/** Search routes to `/search?q=` (agents, creators, tokens, wallets, transactions); "/" focuses it on keyboards. */
function HeaderSearch({ className, autoFocus, onDone }: { className?: string; autoFocus?: boolean; onDone?: () => void }) {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (autoFocus) return
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
      if (t && (t.isContentEditable || /^(input|textarea|select)$/i.test(t.tagName))) return
      if (!ref.current || ref.current.offsetParent === null) return
      e.preventDefault()
      ref.current.focus()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [autoFocus])
  const submit = (e: FormEvent) => {
    e.preventDefault()
    const v = q.trim()
    void navigate({ to: '/search', search: v ? { q: v } : {} })
    ref.current?.blur()
    onDone?.()
  }
  return (
    <form role="search" onSubmit={submit} className={cx('relative items-center', className)}>
      <label className="relative block w-full">
        <span className="sr-only">Search algo.boo</span>
        <Icon name="search" size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-3" />
        <input
          ref={ref}
          type="search"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          autoFocus={autoFocus}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && onDone?.()}
          placeholder="Search agents, tokens, 0x…"
          className="h-9 w-full rounded-[8px] border border-line bg-sunken pl-9 pr-10 text-[16px] text-ink outline-none placeholder:text-ink-3 focus:border-field focus:bg-surface sm:text-[13.5px]"
        />
        <kbd aria-hidden="true" className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded border border-line px-1.5 font-mono text-[10.5px] text-ink-3 lg:block">
          /
        </kbd>
      </label>
    </form>
  )
}

/** The primary navigation from 768px: a fixed rail that never scrolls; icons, with labels from 1024px. */
function Rail({ path }: { path: string }) {
  return (
    <aside data-testid="side-nav" className="fixed bottom-0 left-0 top-[calc(56px+env(safe-area-inset-top))] z-20 hidden w-[72px] border-r border-line bg-bg px-2 py-3 md:block lg:w-[200px] lg:px-3">
      <nav aria-label="Main">
        <ul className="space-y-1">
          {PRIMARY.map((i) => (
            <li key={i.to}>
              <RailLink item={i} path={path} />
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  )
}

function RailLink({ item, path }: { item: NavItem; path: string }) {
  const active = isActive(item, path)
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={cx(
        'rh-hit flex flex-col items-center justify-center gap-1 rounded-[10px] py-2 text-[11px] font-medium transition-colors lg:h-10 lg:flex-row lg:justify-start lg:gap-3 lg:px-3 lg:py-0 lg:text-[14px]',
        active ? 'bg-sunken text-ink' : 'text-ink-3 hover:bg-sunken hover:text-ink',
      )}
    >
      <Icon name={item.icon} size={19} strokeWidth={active ? 2 : 1.75} className={cx('shrink-0', active && 'text-accent')} />
      <span className="truncate">{item.label}</span>
    </Link>
  )
}

/** Bottom tab bar under 768px, above the home indicator; hidden while the on-screen keyboard is open. Desk is the centre. */
function BottomTabs({ path }: { path: string }) {
  return (
    <nav aria-label="Tabs" data-testid="tab-bar" className="rh-tabbar rh-glass fixed inset-x-0 bottom-0 z-30 border-t border-line pb-[env(safe-area-inset-bottom)] md:hidden">
      <ul className="mx-auto grid max-w-[560px] grid-cols-5">
        {PRIMARY.map((t) => {
          const active = isActive(t, path)
          const centre = t.to === '/desk'
          return (
            <li key={t.to}>
              <Link
                to={t.to}
                aria-current={active ? 'page' : undefined}
                data-primary={centre ? 'true' : undefined}
                className={cx('relative flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors', active ? 'text-ink' : 'text-ink-3 active:text-ink')}
              >
                {centre ? (
                  <span aria-hidden="true" className={cx('flex h-8 w-12 items-center justify-center rounded-full transition-colors', active ? 'bg-accent-fill text-on-accent' : 'bg-sunken text-ink')}>
                    <Icon name={t.icon} size={19} strokeWidth={2} />
                  </span>
                ) : (
                  <>
                    <span aria-hidden="true" className={cx('absolute top-0 h-0.5 w-8 rounded-full transition-opacity', active ? 'bg-accent opacity-100' : 'opacity-0')} />
                    <Icon name={t.icon} size={21} strokeWidth={active ? 2 : 1.75} className={active ? 'text-accent' : undefined} />
                  </>
                )}
                {t.label}
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

/** Marketing pages only: one compact row of the reference and legal links. */
function Footer() {
  return (
    <footer className="mb-[calc(56px+env(safe-area-inset-bottom))] border-t border-line md:mb-0" data-testid="footer">
      <nav aria-label="Footer" className="mx-auto flex max-w-[1248px] flex-wrap items-center gap-x-4 px-4 py-4 text-[12.5px] text-ink-3 sm:px-6">
        {[...MORE, ...LEGAL].map((l) => (
          <Link key={l.to} to={l.to} className="inline-flex min-h-11 items-center hover:text-ink md:min-h-8">
            {l.label}
          </Link>
        ))}
        <a href="https://robinhoodchain.blockscout.com" target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center hover:text-ink md:min-h-8">
          Explorer
        </a>
      </nav>
    </footer>
  )
}

/** Page heading block used across routes. */
export function PageHeader({ eyebrow, title, lede, actions, className }: { eyebrow?: ReactNode; title: ReactNode; lede?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={cx('mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between', className)}>
      <div className="min-w-0">
        {eyebrow && <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">{eyebrow}</div>}
        <h1 className="rh-h1 font-display font-semibold text-ink">{title}</h1>
        {lede && <p className="mt-2 max-w-2xl text-[14px] text-ink-2">{lede}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}
